/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  /** tightLeftFocus: box hugs the text on the left (no negative left
   * margin) to avoid an overflow-x clip; text position is unchanged. */
  padding: ${(props) => (props.tightLeftFocus ? '2px 4px 2px 0' : '2px 4px')};
  margin: ${(props) => (props.tightLeftFocus ? '-2px -4px -2px 0' : '-2px -4px')};
  border-radius: 4px;
  /** Keyboard-focus highlight; mouse focus stays quiet. */
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: #F0F4FA;
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  width: 100%;
  background: ${(props) => props.backgroundColor && props.backgroundColor};
  display: flex;
  border-radius: 4px;
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  color: ${(props) => (props.selected ? '#9E9E9E' : '#444444')};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdown = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  setPeakPowerEnum,
  peakPowerEnum,
  name,
  unit,
  setIsBodyweight,
  setIsTime,
  tightLeftFocus,
}) => {
  const menuConfig = [
    {
      condition: rowName === 'reps' || rowName === 'time',
      items: [
        {
          label: 'Time',
          selected: primaryRowSwitch.time,
          action: () => {
            const newSwitch = { ...primaryRowSwitch };
            newSwitch.reps = false;
            newSwitch.time = true;
            setPrimaryRowSwitch(newSwitch);
            setIsTime(true);
            updateColumnName(rowName, 'time');
          },
        },
        {
          label: 'Reps',
          selected: primaryRowSwitch.reps,
          action: () => {
            const newSwitch = { ...primaryRowSwitch };
            newSwitch.time = false;
            newSwitch.reps = true;
            setPrimaryRowSwitch(newSwitch);
            setIsTime(false);
            updateColumnName(rowName, 'reps');
          },
        },
      ],
    },
    {
      condition: rowName === 'peak power',
      items: [
        {
          label: 'Single',
          selected: peakPowerEnum === 1,
          action: () => setPeakPowerEnum(1),
        },
        {
          label: 'Left/Right',
          selected: peakPowerEnum === 2,
          action: () => setPeakPowerEnum(2),
        },
      ],
    },
    {
      condition: rowName === 'load' || rowName === 'bodyweight',
      items: [
        {
          label: 'Load',
          selected: primaryRowSwitch.load,
          action: () => {
            const newSwitch = { ...primaryRowSwitch };
            newSwitch.bodyweight = false;
            newSwitch.load = true;
            setPrimaryRowSwitch(newSwitch);
            setIsBodyweight(false);
            updateColumnName(rowName, 'load');
          },
        },
        {
          label: 'Bodyweight',
          selected: primaryRowSwitch.bodyweight,
          action: () => {
            const newSwitch = { ...primaryRowSwitch };
            newSwitch.load = false;
            newSwitch.bodyweight = true;
            setPrimaryRowSwitch(newSwitch);
            setIsBodyweight(true);
            updateColumnName(rowName, 'bodyweight');
          },
        },
      ],
    },
  ];

  const isDeleteOnly = rowName === 'velocity' || rowName === 'tempo' || rowName === 'rest' || rowName === '%pr';

  /** Delete action used by the delete-only menu (rest/tempo/velocity/%pr). */
  const deleteOnlyAction = () => {
    const flippedStatus = !primaryRowSwitch[rowName];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[rowName] = flippedStatus;
    setPrimaryRowSwitch(newSwitch);
  };

  // Get the format options based on rowName
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const mainMenuConfig = [
    // Include format items directly in the main menu
    ...(rowName !== 'velocity' && rowName !== 'tempo' && rowName !== 'rest' && rowName !== '%pr'
      ? formatItems.map((item) => ({
        label: item.label,
        selected: item.selected,
        action: item.action,
        // icon: 'swap',
      }))
      : []),
    // Add divider if there are format items and we'll have a delete option
    ...(formatItems.length > 0 && rowName !== 'reps' && rowName !== 'time'
      ? [{ divider: true }]
      : []),
    {
      label: 'Delete',
      /** should not be able to delte reps/time field
       * this is a required field for lifts
       */
      condition: rowName !== 'reps' && rowName !== 'time',
      icon: 'trashcan',
      color: '#FF0505',
      action: () => {
        const flippedStatus = !primaryRowSwitch[rowName];
        const newSwitch = { ...primaryRowSwitch };
        newSwitch[rowName] = flippedStatus;
        /** if a user deletes load/bodyweight we want to
         * reset the name to load in the add properties dropdown
         */
        if (rowName === 'bodyweight' && !newSwitch.bodyweight) {
          setIsBodyweight(false);
          updateColumnName(rowName, 'load');
        }
        setPrimaryRowSwitch(newSwitch);
      },
    },
  ];

  /** Controlled open state + keyboard navigation. rc-dropdown/rc-menu
   * don't move focus into the overlay on open, so arrow/Enter nav is
   * handled here (mirrors StaticAddColumnDropdownComponent). */
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const navItems = isDeleteOnly
    ? [{ label: 'Delete', action: deleteOnlyAction }]
    : mainMenuConfig.filter((i) => !i.divider && i.condition !== false);

  useEffect(() => {
    if (open) setFocusedIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = navItems[focusedIndex];
        if (item) {
          item.action();
          setOpen(false);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, focusedIndex, navItems]);

  const menu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        gap: 8,
        background: 'white',
      }}
    >
      <MenuWrapper>
        {(() => {
          let navIndex = -1;
          return mainMenuConfig
            .filter((item) => item.condition !== false) // Filter items where condition is false
            .map((item, index) => {
              if (item.divider) return <CustomDivider key={`divider-${index}`} />;
              navIndex += 1;
              const isFocused = navIndex === focusedIndex;
              return (
                <MenuItem
                  key={item.label || `item-${index}`}
                  style={{
                    cursor: 'pointer',
                    background: isFocused ? '#F5F5F5' : 'white',
                  }}
                  /** Close on selection like the keyboard Enter path — with a
                   * controlled `visible` rc-dropdown won't close itself, and a
                   * lingering open menu keeps the window keydown listener
                   * attached (a stray Enter would re-fire an action). */
                  onClick={() => { item.action(); setOpen(false); }}
                >
                <IconContainer>
                  {item?.icon && (
                  <IcomoonReact
                    iconSet={iconSet}
                    size={17}
                    icon={item.icon}
                    color={item.color || '#9E9E9E'}
                    style={{ marginRight: 5 }}
                  />
                  )}
                  <ArrowDiv>
                    <MenuText selected={item.selected}>{item.label}</MenuText>
                    {item.selected && (
                      <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='#9E9E9E' />
                    )}
                  </ArrowDiv>
                </IconContainer>
                </MenuItem>
              );
            });
        })()}
      </MenuWrapper>
    </Menu>
  );

  const deleteOnlyMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        background: 'white',
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{ cursor: 'pointer', background: focusedIndex === 0 ? '#F5F5F5' : 'white' }}
          onClick={() => { deleteOnlyAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={['click']}
      visible={open}
      onVisibleChange={setOpen}
      overlay={isDeleteOnly ? deleteOnlyMenu : menu}
      overlayStyle={{ zIndex: 1101 }}
      aria-haspopup='true'
    >
      <DropdownContainerPrimary
        role='button'
        tabIndex={0}
        tightLeftFocus={tightLeftFocus}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          /** Open on Enter/Space/ArrowDown when closed; once open the
           * window listener owns arrow/Enter nav. stopPropagation keeps
           * the opening key from reaching that listener. */
          if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }
        }}
      >
        <DropdownIcon padding={size}>
          {name === '%pr' ? '%PR' : name === 'Time' ? 'Time(s)' : name}
          {' '}
          {unit || null}
          <IcomoonReact
            iconSet={iconSet}
            size={7}
            style={{ minWidth: '7px' }}
            icon='down-arrow'
            color='black'
          />
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdown.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  setPeakPowerEnum: PropTypes.func.isRequired,
  peakPowerEnum: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  setIsBodyweight: PropTypes.func.isRequired,
  setIsTime: PropTypes.func.isRequired,
  tightLeftFocus: PropTypes.bool,
};

SwapPropDropdown.defaultProps = {
  tightLeftFocus: false,
};

export default SwapPropDropdown;
